import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errorMessage } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/crm/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/crm/dashboard");
    } catch (err) {
      setError(errorMessage(err, "Correo o contraseña incorrectos"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen bg-[#F5F7FA] lg:grid-cols-2" data-testid="login-page">
      <div className="grain relative hidden overflow-hidden bg-[#0B2545] p-12 text-white lg:flex lg:flex-col lg:justify-between">
        <Logo light variant="login" />
        <div className="relative">
          <h1 className="font-display text-4xl font-extrabold leading-tight">Gestión comercial<br />con precisión clínica.</h1>
          <p className="mt-4 max-w-sm text-white/60">Cada lead asignado, cada seguimiento visible.</p>
        </div>
        <p className="text-xs text-white/40">Acceso exclusivo para personal autorizado.</p>
      </div>
      <div className="flex items-center justify-center px-5 py-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="mb-10 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#1E6FD9]" data-testid="login-back-link">
            <ArrowLeft className="h-4 w-4" /> Volver al sitio
          </Link>
          <div className="flex justify-center lg:hidden"><Logo variant="login" /></div>
          <h2 className="mt-6 font-display text-3xl font-extrabold text-[#0B2545]">Acceso al sistema</h2>
          <p className="mt-2 text-sm text-slate-500">Ingresa con tu correo y contraseña.</p>
          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs font-semibold text-slate-600">Correo</Label>
              <Input id="email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                     className="mt-1.5 h-11 rounded-xl bg-white" data-testid="login-email-input" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-semibold text-slate-600">Contraseña</Label>
              <Input id="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required
                     className="mt-1.5 h-11 rounded-xl bg-white" data-testid="login-password-input" />
            </div>
            {error && <p className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} className="h-11 w-full rounded-xl bg-[#1E6FD9] hover:bg-[#0B2545]" data-testid="login-submit-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Lock className="mr-2 h-4 w-4" /> Ingresar</>}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
